import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Heart,
  Menu,
  X,
  Sparkles,
  LogOut,
  Globe,
  Crown,
  Shield,
  Smartphone,
  ChevronDown,
  Play,
  BookOpen,
  ChefHat,
  HelpCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MembershipDialog } from './MembershipDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

// Custom Logo Component - Hrniec so srdcom
const LogoIcon = ({ className = "w-8 h-8" }) => (
  <img
    src="/logo.png"
    alt="S Luciou v kuchyni logo"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

export const Navbar = () => {
  const { user, logout, language, setLanguage, t } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [klubMenuOpen, setKlubMenuOpen] = React.useState(false);
  const navigate = useNavigate();

  // Primary navigation visible to everyone
  const primaryLinks = [
    { href: '/', label: t('home') },
    { href: '/recipes', label: t('recipes') },
  ];

  // "Klub" dropdown — member content grouped together
  const klubLinks = [
    {
      href: '/magicka-patka',
      label: language === 'sk' ? 'Magická päťka' : 'Magická pětka',
      desc: language === 'sk' ? 'Videokurz + sprievodný ebook' : 'Videokurz + doprovodný ebook',
      icon: Play,
    },
    {
      href: '/tematicke-kolekcie',
      label: language === 'sk' ? 'Tematické kolekcie' : 'Tematické kolekce',
      desc: language === 'sk' ? '12 ebookov — sezónne, tematické, bonus' : '12 ebooků — sezónní, tematické, bonus',
      icon: BookOpen,
    },
  ];

  return (
    <nav className="sticky top-0 z-50 glass border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="w-12 h-12 flex items-center justify-center group-hover:scale-105 transition-transform">
              <LogoIcon className="w-12 h-12" />
            </div>
            <div className="hidden sm:block">
              <span className="font-serif text-lg font-semibold text-text-heading">S Luciou v kuchyni</span>
              <p className="font-accent text-sm text-primary -mt-0.5">klub žien, čo varia srdcom</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {primaryLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-muted-foreground hover:text-primary transition-colors font-medium"
                data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Klub dropdown */}
            <DropdownMenu open={klubMenuOpen} onOpenChange={setKlubMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors font-medium"
                  data-testid="nav-klub"
                >
                  <Crown className="w-4 h-4" />
                  {language === 'sk' ? 'Klub' : 'Klub'}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${klubMenuOpen ? 'rotate-180' : ''}`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                  {language === 'sk' ? 'Členský obsah' : 'Členský obsah'}
                </DropdownMenuLabel>
                {klubLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <DropdownMenuItem
                      key={link.href}
                      onClick={() => navigate(link.href)}
                      className="flex items-start gap-3 py-3 cursor-pointer"
                      data-testid={`nav-klub-${link.href.replace('/', '')}`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-text-heading">{link.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{link.desc}</p>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/ai-generator"
              className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors font-medium"
              data-testid="nav-ai-generator"
            >
              <Sparkles className="w-4 h-4" />
              {t('aiGenerator')}
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 hidden sm:inline-flex" data-testid="language-switcher">
                  <Globe className="w-4 h-4" />
                  <span className="uppercase text-xs font-medium">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('sk')} className={language === 'sk' ? 'bg-muted' : ''}>
                  🇸🇰 Slovenčina
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('cz')} className={language === 'cz' ? 'bg-muted' : ''}>
                  🇨🇿 Čeština
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <>
                {/* Favorites */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/favorites')}
                  className="hidden sm:flex"
                  data-testid="nav-favorites"
                >
                  <Heart className="w-4 h-4 mr-1.5" />
                  {t('favorites')}
                </Button>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu">
                      <Avatar className="w-7 h-7">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-primary text-white text-xs">
                          {user.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden sm:inline text-sm font-medium">{user.name?.split(' ')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <p className="text-sm font-medium leading-none">{user.name}</p>
                      <p className="text-xs text-muted-foreground mt-1 truncate">{user.email}</p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="user-menu-favorites">
                      <Heart className="w-4 h-4 mr-2" />
                      {t('favorites')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/install-app')} data-testid="user-menu-install">
                      <Smartphone className="w-4 h-4 mr-2" />
                      {language === 'sk' ? 'Apka na mobil' : 'Apka na mobil'}
                    </DropdownMenuItem>
                    {(user.membership_type === 'admin' || user.membership_type === 'vip') && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin-link">
                        <Shield className="w-4 h-4 mr-2" />
                        {language === 'sk' ? 'Admin panel' : 'Admin panel'}
                      </DropdownMenuItem>
                    )}
                    {(user.membership_type === 'admin' || user.membership_type === 'vip') && (
                      <DropdownMenuItem onClick={() => navigate('/help')} data-testid="nav-help-link">
                        <HelpCircle className="w-4 h-4 mr-2" />
                        {language === 'sk' ? 'Nápoveda' : 'Nápověda'}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="user-menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <div className="hidden sm:inline-block">
                  <MembershipDialog
                    trigger={
                      <Button
                        className="bg-primary hover:bg-primary-hover text-white rounded-full px-5"
                        data-testid="navbar-buy-membership-link"
                      >
                        {language === 'sk' ? 'Kúpiť členstvo' : 'Koupit členství'}
                      </Button>
                    }
                  />
                </div>
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="rounded-full px-5 border-2"
                  data-testid="login-button"
                >
                  {t('login')}
                </Button>
              </div>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/50 animate-slide-up">
            <div className="flex flex-col gap-1">
              {primaryLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="px-3 py-2.5 rounded-lg text-text-heading hover:bg-muted transition-colors font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.href.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}

              <div className="pt-2 mt-1 border-t border-border/50">
                <p className="px-3 pt-2 pb-1 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                  {language === 'sk' ? 'Klub' : 'Klub'}
                </p>
                {klubLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-text-heading hover:bg-muted transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${link.href.replace('/', '')}`}
                    >
                      <Icon className="w-4 h-4 text-primary" />
                      <span>{link.label}</span>
                    </Link>
                  );
                })}
              </div>

              <Link
                to="/ai-generator"
                className="flex items-center gap-2 px-3 py-2.5 mt-1 rounded-lg text-text-heading hover:bg-muted transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-ai-generator"
              >
                <Sparkles className="w-4 h-4 text-primary" />
                {t('aiGenerator')}
              </Link>

              {user && (
                <>
                  <div className="pt-2 mt-1 border-t border-border/50">
                    <Link
                      to="/favorites"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-text-heading hover:bg-muted transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Heart className="w-4 h-4 text-primary" />
                      {t('favorites')}
                    </Link>
                    <Link
                      to="/install-app"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-text-heading hover:bg-muted transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Smartphone className="w-4 h-4 text-primary" />
                      {language === 'sk' ? 'Apka na mobil' : 'Apka na mobil'}
                    </Link>
                  </div>
                </>
              )}

              {/* Language switcher mobile */}
              <div className="pt-2 mt-1 border-t border-border/50 flex gap-2 px-3">
                <button
                  onClick={() => setLanguage('sk')}
                  className={`flex-1 py-2 rounded-lg text-sm ${language === 'sk' ? 'bg-primary text-white' : 'bg-muted'}`}
                >
                  🇸🇰 SK
                </button>
                <button
                  onClick={() => setLanguage('cz')}
                  className={`flex-1 py-2 rounded-lg text-sm ${language === 'cz' ? 'bg-primary text-white' : 'bg-muted'}`}
                >
                  🇨🇿 CZ
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Logo
const FooterLogo = () => (
  <img src="/logo.png" alt="S Luciou v kuchyni" className="w-10 h-10 object-contain" />
);

export const Footer = () => {
  const { t } = useAuth();

  return (
    <footer className="bg-text-heading text-white py-12 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
              <FooterLogo />
            </div>
            <div>
              <span className="font-serif text-xl font-semibold">S Luciou v kuchyni</span>
              <p className="font-accent text-primary text-lg">{t('clubOfWomen')}</p>
            </div>
          </div>
          <div className="text-center md:text-right">
            <p className="text-primary-foreground/80 text-sm mb-1">Varíme podľa Magickej päťky</p>
            <p className="text-primary-foreground/60 text-xs">{t('footerText')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
